'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { mapProduct } from 'helpers/algolia/map-product';
import { canonicalAttributePath } from 'helpers/algolia/attribute-paths';
import { trackEvent } from 'helpers/customEvents';
import { hasHtmlContent, sanitize } from 'helpers/utils/htmlSanitize';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import ScrollToTopButton from 'components/commercetools-ui/atoms/scroll-to-top';
import ProductTile from '../product-tile';
import ViewToggle from '../view-toggle';
import CurrentRefinements from './components/current-refinements';
import CollectionFacets from './components/collection-facets';
import DesktopFacets from './components/desktop-facets';
import MobileFacets from './components/mobile-facets';
import DesktopSortFacets from './components/desktop-facets/sort-facets';
import StaticProductTile from './components/static-product-tile';
import SsrPagination from './components/pagination';
import SsrLoadMore from './components/load-more';
import SsrInfiniteScroll from './components/infinite-scroll';
import { useTileLoading, TILE_PAGE_SIZE, type TileLoadingStyle } from './hooks/useTileLoading';
import { useCurrentRefinements, useDatasetStatus, useHits, useInstantSearch } from './local-store';
import { useProductList } from './context';
import { FacetConfiguration, PriceConfiguration } from './types';

interface Props {
  /** Last URL segment: keys the persisted filters (localStorage) and names the listing in tracking. */
  slug?: string;
  /** The listing's H1 — the CMS `listing_title`. */
  title: string;
  /** Rich text under the H1 — the CMS `listing_description`. Omitted/blank renders nothing. */
  description?: string;
  facetsConfiguration: Record<string, FacetConfiguration>;
  pricesConfiguration: Record<string, PriceConfiguration>;
  /** CMS `listing_type` "Collection": collection chrome (facet pills, no sidebar) + isCollectionPage tiles. */
  isCollection?: boolean;
  /** CMS product_tiles_loading_style: 'infinite' | 'loadMore' | 'pagination' | 'all'. */
  loadingStyle?: TileLoadingStyle;
}

/**
 * Server-side-rendered Product Listing Page (SERVER PATH — every V2 listing block, never /search).
 *
 * The dataset is preloaded on the server (Redis-cached) and provided via <LocalDatasetProvider> in
 * cms-components-v2/section-with-product-listing, but only the FIRST TILE PAGE of it reaches the SSR
 * HTML — exactly the tiles the grid shows on arrival, in the default "new first" order. The provider pulls the remaining products in a
 * single XHR right after hydration; filtering, sorting and facet counts are then recomputed in the
 * browser from that hydrated dataset (no further Algolia calls). Only the visible batch mounts the
 * interactive <ProductTile>; loaded-but-off-screen tiles render a hook-free <StaticProductTile> and
 * swap in as they scroll into range.
 *
 * Until the top-up lands the grid holds a PREFIX of the listing, so this component must not act on
 * the partial set: persisted refinements are not restored and the reveal controls are inert until
 * `isComplete`. Counts and pagination read `totalHits`, which is correct from first paint.
 */
const ProductListSSR: React.FC<Props> = ({
  slug,
  title,
  description,
  facetsConfiguration,
  pricesConfiguration,
  isCollection,
  loadingStyle,
}) => {
  const [isTabletSize] = useMediaQuery(desktop);
  const { locale } = useParams();

  const { hits } = useHits();
  const { items: currentRefinements } = useCurrentRefinements();
  const { totalHits, isComplete, queryToken } = useDatasetStatus();
  const { setUiState } = useInstantSearch();

  const activeColour = useMemo(() => {
    const colourGroup = currentRefinements.find((item) =>
      item.attribute.toLowerCase().includes('colour') || item.attribute.toLowerCase().includes('color')
    );
    const ref = colourGroup?.refinements?.[0];
    return (ref?.value ?? ref?.label) as string | undefined;
  }, [currentRefinements]);

  const { updateFacetsConfiguration, updatePricesConfiguration } = useProductList();

  const [currentView, setCurrentView] = useState<'grid' | 'list'>('grid');
  const handleViewChange = (view: 'grid' | 'list') => setCurrentView(view);

  // Push the resolved facet/price configuration into the SSR product-list context so the facet
  // components (DesktopFacets/RangeFacet/…) can read it.
  useEffect(() => {
    updateFacetsConfiguration(facetsConfiguration);
    updatePricesConfiguration(pricesConfiguration);
  }, [facetsConfiguration, pricesConfiguration, updateFacetsConfiguration, updatePricesConfiguration]);

  // ---- Filter persistence (localStorage) — survives navigation/refresh ----
  // Same key convention as the client PLP so filters carry across the client↔SSR transition.
  const storageKey = useMemo(() => `product_list_facet_filters_${locale}_${slug || 'all'}`, [locale, slug]);
  const hasHydratedRefinements = useRef(false);
  const [persistedFacetFilters, setPersistedFacetFilters] = useState<string[][]>([]);

  // Load saved filters on mount.
  useEffect(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
      if (saved) setPersistedFacetFilters(JSON.parse(saved));
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  // Persist current filters whenever they change.
  useEffect(() => {
    try {
      const groupedByAttribute = currentRefinements
        .map((facetItem) => (facetItem?.refinements || []).map((ref) => `${facetItem.attribute}:${ref.value}`))
        .filter((arr) => Array.isArray(arr) && arr.length > 0);
      if (groupedByAttribute.length > 0) window.localStorage.setItem(storageKey, JSON.stringify(groupedByAttribute));
      else window.localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }, [currentRefinements, storageKey]);

  // Restore saved filters into the store once (after the load effect populated persistedFacetFilters).
  // Held back until the dataset is complete — restoring against the SSR prefix would filter 12 hits
  // and typically show "No products found" for as long as the top-up takes, then flip to the real
  // result. Waiting costs nothing visible: unrestored, the grid shows the correct unfiltered page 1.
  useEffect(() => {
    if (!isComplete) return;
    if (hasHydratedRefinements.current) return;
    const saved = persistedFacetFilters;
    if (!Array.isArray(saved) || saved.length === 0) return;
    const refinementList: Record<string, string[]> = {};
    for (const group of saved) {
      for (const token of group) {
        const [rawAttribute, ...rest] = String(token).split(':');
        const value = rest.join(':');
        if (!rawAttribute || value === undefined) continue;
        // Entries persisted before facet paths were canonicalised carry the Algolia-facing
        // `searchableAttributes.*` spelling. Rewriting on restore keeps them working: an uncanonical
        // path resolves to no values, and a refinement that matches nothing empties the whole grid.
        const attribute = canonicalAttributePath(rawAttribute);
        if (!refinementList[attribute]) refinementList[attribute] = [];
        if (!refinementList[attribute].includes(value)) refinementList[attribute].push(value);
      }
    }
    if (Object.keys(refinementList).length > 0) {
      setUiState((prev: any) => Object.fromEntries(Object.keys(prev ?? {}).map((k) => [k, { refinementList }])));
      hasHydratedRefinements.current = true;
    }
  }, [isComplete, persistedFacetFilters, setUiState]);

  useEffect(() => {
    hasHydratedRefinements.current = false;
  }, [storageKey]);

  const hasResults = hits.length > 0;
  const hasDescription = hasHtmlContent(description);
  const bg = isCollection ? 'bg-[#F8F8F8]' : 'bg-[#F3EDE9]';

  // Tile loading style: the first tile page renders server-side, the rest arrive with the dataset
  // top-up. Loaded-but-out-of-view tiles are `hidden` (kept in the DOM; lazy images load only when
  // shown).
  //
  // The page maths run on `listingTotal`, not on how many hits happen to be loaded: while the top-up
  // is in flight `hits` is a 12-item prefix, and sizing the control to that would render "page 1 of 1"
  // and then visibly grow a moment later. `totalHits` is known from first paint, so the pagination
  // control is complete and stable immediately — it just stays inert (below) until the hits are real.
  const gridRef = useRef<HTMLDivElement | null>(null);
  const listingTotal = isComplete ? hits.length : Math.max(hits.length, totalHits);
  const { totalPages, currentPage, setPage, visibleCount, loadMore, isHidden } = useTileLoading(
    listingTotal,
    loadingStyle ?? 'infinite',
    queryToken,
  );
  // `pagination_click` — same payload as the (now unused) client-InstantSearch call sites, so the
  // existing GTM trigger and variables keep working. `type` says which control produced it:
  // 'pagination' | 'load_more' | 'infinite_scroll'. Only emitted when the view actually advances,
  // so re-clicking the current page, or a sentinel that re-intersects with nothing left to reveal,
  // does not inflate the count.
  const listingName = slug || 'all';
  const trackPagination = (pageNumber: number, type: 'pagination' | 'load_more' | 'infinite_scroll') => {
    trackEvent('pagination_click', {
      page_number: `${pageNumber}`,
      collection_name: listingName,
      pageContext: listingName,
      type,
    });
  };

  const goToPage = (next: number) => {
    // Never page into hits that have not arrived yet — the control is disabled while incomplete, so
    // this is the belt-and-braces half of the same guard.
    if (!isComplete) return;
    if (next === currentPage || next < 1 || next > totalPages) return;
    trackPagination(next, 'pagination');
    setPage(next);
    // Wait for the page's tile swap (previous page → display:none, new page revealed) to fully
    // commit before scrolling — doing it synchronously (or on the next frame) lets that layout
    // change disrupt the in-flight smooth scroll, so it settled at the very top under the ~119px
    // sticky header. With a stable layout, scroll-margin-top lands the first tile just below it.
    setTimeout(() => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  /** Reveal the next batch. `page_number` is the batch about to be shown (1 = the SSR-visible one). */
  const revealMore = (type: 'load_more' | 'infinite_scroll') => {
    // Bounded by LOADED hits, not by `listingTotal`: revealing past what the top-up has delivered
    // would show empty space and fire a bogus `pagination_click`.
    if (!isComplete || visibleCount >= hits.length) return;
    trackPagination(Math.floor(visibleCount / TILE_PAGE_SIZE) + 1, type);
    loadMore();
  };

  const Tiles = () => (
    <>
      {hits.map((hit: any, i: number) => {
        const product = mapProduct(hit, locale as string, undefined, activeColour);
        const hidden = isHidden(i);
        return (
          // Off-screen (`hidden`) tiles render a lightweight, hook-free static tile (crawlable, ~no
          // hydration); only the visible batch mounts the interactive ProductTile. `contents` keeps
          // the tile a direct grid child so the grid layout is unaffected.
          <div key={hit.objectID ?? hit.slug ?? i} className={hidden ? 'hidden' : 'contents'}>
            {hidden ? (
              <StaticProductTile product={product} locale={locale as string} />
            ) : (
              <ProductTile
                product={product}
                layout={currentView}
                isCategoryPage={!isCollection}
                isCollectionPage={isCollection}
              />
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <>
      <div id="plp-container" className={bg}>
        <div id="container-scroll" className="flex flex-col items-end">
          <h1
            className={`w-full pt-48 text-center text-32 font-medium leading-8 text-[#090909] md:text-40 2xl:pt-80 2xl:text-48 ${
              hasDescription ? 'pb-16 2xl:pb-24' : 'pb-32 2xl:pb-48'
            }`}
          >
            {title}
          </h1>
          {/* CMS `listing_description` — centred under the H1, held to a readable measure rather than
              the full grid width. The H1's own bottom padding shrinks above so the two read as one
              block. */}
          {hasDescription && (
            <div
              className="mx-auto w-full max-w-[60rem] px-24 pb-32 text-center text-14 font-normal leading-6 text-[#333] md:text-16 2xl:pb-48 2xl:text-18 [&>*:first-child]:!mt-0 [&>*:last-child]:!mb-0 [&_p]:my-8"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          )}
          {/*
            Collection PLPs use a different chrome from category PLPs: a centred row of facet pills under
            the heading, and no sidebar, sort control, view toggle or refinement chips. That is how the
            collection page looked before it moved onto this shared SSR grid, so it is reproduced here
            rather than inheriting the category layout.
          */}
          {isCollection ? (
            <div className="w-full px-24 pb-8 md:px-28 lg:px-48 xl:px-60 2xl:px-80">
              <CollectionFacets facetsConfiguration={facetsConfiguration} />
            </div>
          ) : (
            <div className="px:24 mt-8 flex w-full items-center justify-between gap-48 lg:px-48 xl:px-60 2xl:px-80">
              <div className="lg:ml-[16.5rem] 2xl:ml-[23rem]">
                <CurrentRefinements />
              </div>
              <div className="hidden items-center gap-4 lg:flex">
                <DesktopSortFacets />
                <ViewToggle onViewChange={handleViewChange} />
              </div>
            </div>
          )}
        </div>

        <div
          className={`items-start gap-24 px-24 pt-24 md:flex md:flex-row md:px-28 lg:px-48 xl:px-60 2xl:gap-48 2xl:px-80 ${
            !hasResults ? 'pb-0' : 'pb-48 xl:pb-0 2xl:pb-48'
          }`}
        >
          {!isCollection && (
            <div className={`${!hasResults ? '' : 'lg:block'} hidden ${bg} lg:min-w-[15rem] 2xl:min-w-[20rem]`}>
              <DesktopFacets facetsConfiguration={facetsConfiguration} />
            </div>
          )}

          {/* scroll-mt clears the ~119px sticky header so pagination lands the first tile just BELOW
              it (not hidden behind it) when goToPage scrollIntoView's this element. */}
          <div ref={gridRef} className={`w-full scroll-mt-[100px] ${bg} lg:scroll-mt-[130px]`}>
            {!isCollection && (
              <div className={`${!hasResults && 'hidden'} flex items-center justify-between gap-2 lg:block`}>
                <div className="block lg:hidden">
                  <div className="cursor-pointer bg-transparent py-4 text-[14px]">
                    <ViewToggle onViewChange={handleViewChange} />
                  </div>
                </div>
                <MobileFacets facetsConfiguration={facetsConfiguration} />
              </div>
            )}

            {!hasResults ? (
              <p className="results-zero no-result-container py-60 text-center text-22">No products found</p>
            ) : currentView === 'list' ? (
              <div className="overflow-x-auto lg:overflow-visible">
                <div className="hidden text-[15px] font-semibold text-gray-800 lg:flex">
                  <div className="w-[60%] px-6 py-4 text-left">Product</div>
                  <div className="w-[20%] px-6 py-4 text-left">Code</div>
                  <div className="w-[20%] px-6 py-4 text-left">RRP</div>
                </div>
                <div className="flex flex-col pt-32 lg:pt-0">
                  <Tiles />
                </div>
              </div>
            ) : (
              <div
                className={
                  isTabletSize
                    ? 'grid grid-cols-1 gap-16 pt-32 md:grid-cols-4 lg:grid-cols-3 lg:pt-0 xl:grid-cols-4'
                    : 'grid gap-16 pt-32 md:grid-cols-2 lg:pt-0 xl:grid-cols-3'
                }
              >
                <Tiles />
              </div>
            )}

            {/* `listingTotal` sizes these to the whole listing from first paint; `disabled` keeps them
                inert for the moment before the remaining hits land. Infinite scroll needs no disabled
                flag — withholding `hasMore` already stops the sentinel from being observed. */}
            {hasResults && loadingStyle === 'pagination' && (
              <SsrPagination page={currentPage} totalPages={totalPages} onChange={goToPage} disabled={!isComplete} />
            )}
            {hasResults && loadingStyle === 'loadMore' && (
              <SsrLoadMore
                shown={visibleCount}
                total={listingTotal}
                onLoadMore={() => revealMore('load_more')}
                disabled={!isComplete}
              />
            )}
            {hasResults && (loadingStyle === 'infinite' || !loadingStyle) && (
              <SsrInfiniteScroll
                onLoadMore={() => revealMore('infinite_scroll')}
                hasMore={isComplete && visibleCount < hits.length}
              />
            )}
          </div>
        </div>
      </div>
      <ScrollToTopButton />
    </>
  );
};

export default ProductListSSR;
